import { chunk } from 'lodash';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { connectSyncPair, deleteSyncPair, getSyncPairs } from '~/api/evisync';
import { getStorageIntegrations } from '~/common/constants/integrations';
import { showToast } from '~/components/Shared/EcToast/showToast';
import { PersistedTable } from '~/components/Shared/PersistedTable/PersistedTable';
import ProviderLogo from '~/components/Shared/ProviderLogo/ProviderLogo';
import { Button } from '~/eds/src/components/Button/Button';
import { Chip } from '~/eds/src/components/Chip/Chip';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { ManageSyncPairStageType } from '~/enums/ManageSyncPairStageType';
import { QueryParamType } from '~/enums/QueryParamType';
import { SyncPairStatusType } from '~/enums/SyncPairStatusType';
import { TableContextType } from '~/enums/TableContextType';
import { StorageConfigurationPanel } from '~/features/integrations/storage/StorageConfigurationPanel';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useAsync } from '~/hooks/useAsync';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useIntl } from 'react-intl';
import { slices } from '~/redux/slices';
import { useRouting } from '~/routing/useRouting';
import { ERROR, SUCCESS } from '~/types/toast.types';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import Folder from '~/ui/components/Folder/Folder';
import Text from '~/ui/components/Text/Text';
import { useModal } from '~/ui/hooks/modal';
import { removeQueryParam } from '~/utils/browser';
import { fileTypesToChips } from '~/utils/fileTypesToChips';
import { createSyncPairData } from '~/utils/integrations';
import { getPageSearchQueryByKey } from '~/utils/searchQuery';
import { testIsSuperAdmin } from '~/utils/user';

import { buildProviderPath, resetSyncPairAuthData } from '../util';
import EmptySyncPairsPlaceholder from './EmptySyncPairsPlaceholder';

function StorageIntegrations() {
  const intl = useIntl();
  const dispatch = useDispatch();
  const currentUser = useCurrentUser();

  const enableSkipEmptyFolders = useFlag(FlagType.SkipEmptyFolders);
  const enableConfigPanel = useFlag(FlagType.StorageConfiguration);

  const activeSyncPair = useSelector(
    slices.integrations.selectors.selectActiveSyncPair,
  );
  const storageSyncPairs = useSelector(
    slices.integrations.selectors.selectSyncPairs,
  );
  const storageIntegrations = getStorageIntegrations();
  const syncPairs = Object.values(storageSyncPairs).map((syncPair) => ({
    ...storageIntegrations[syncPair.provider],
    ...syncPair,
  }));
  const tableState = useSelector(
    slices.integrations.selectors.selectTableState,
  );

  const [syncPairToDelete, setSyncPairToDelete] = useState(null);
  const [activeConfig, setActiveConfig] = useState(null);
  const [activeTab, setActiveTab] = useState(null);

  const { location, navigate, params } = useRouting();

  const { clientId } = params;

  const states = getPageSearchQueryByKey(QueryParamType.States, btoa('{}'));
  const { provider, state, code, syncPairId } = JSON.parse(atob(states));
  const { pageIndex, pageSize } = tableState;

  const columns = [
    {
      key: 'provider',
      title: intl.formatMessage({
        id: 'StorageIntegrations.typeColumn',
        defaultMessage: 'Type',
        description: 'Column header for provider type',
      }),
      renderCell: ({ name, provider }) => (
        <Layout spacing={2} align="center">
          <ProviderLogo provider={provider} size="s" />
          <Text>{name}</Text>
        </Layout>
      ),
    },
    {
      key: 'status',
      cellType: 'chips',
      title: intl.formatMessage({
        id: 'StorageIntegrations.statusColumn',
        defaultMessage: 'Status',
        description: 'Column header for sync pair status',
      }),
      mapCellProps: (props) => {
        let icon;
        let text;
        let status;
        switch (props.status) {
          case SyncPairStatusType.AuthError:
            text = intl.formatMessage({
              id: 'StorageIntegrations.authenticationError',
              defaultMessage: 'Authentication error',
              description: 'Status message for authentication errors',
            });
            status = 'danger';
            break;
          case SyncPairStatusType.OtherError:
            text = intl.formatMessage({
              id: 'StorageIntegrations.providerError',
              defaultMessage: 'Provider error',
              description: 'Status message for provider errors',
            });
            status = 'danger';
            break;
          case SyncPairStatusType.DataAccessError:
            text = intl.formatMessage({
              id: 'StorageIntegrations.dataAccessError',
              defaultMessage: 'Data access error',
              description: 'Status message for data access errors',
            });
            status = 'danger';
            break;
          case SyncPairStatusType.OutOfSpaceError:
            text = intl.formatMessage({
              id: 'StorageIntegrations.outOfSpaceError',
              defaultMessage: 'Out of space error',
              description: 'Status message for out of space errors',
            });
            status = 'danger';
            break;
          case SyncPairStatusType.Active:
            text = intl.formatMessage({
              id: 'StorageIntegrations.active',
              defaultMessage: 'Active',
              description: 'Status message for active sync pairs',
            });
            status = 'success';
            break;
          case SyncPairStatusType.Initial:
            icon = 'loading';
            text = intl.formatMessage({
              id: 'StorageIntegrations.initiating',
              defaultMessage: 'Initiating',
              description: 'Status message for initiating sync pairs',
            });
            status = 'info';
            break;
          default:
            text = intl.formatMessage({
              id: 'StorageIntegrations.unknownError',
              defaultMessage: 'Unknown Error',
              description: 'Status message for unknown errors',
            });
            status = 'danger';
        }
        const statusChip = {
          icon,
          text,
          status,
        };
        return {
          chips: [statusChip],
        };
      },
    },
    {
      key: 'providerFolderPath',
      title: intl.formatMessage({
        id: 'StorageIntegrations.externalFolder',
        defaultMessage: 'External Folder',
        description: 'Column header for external folder path',
      }),
      renderCell: ({
        id,
        name,
        providerFolderPath,
        providerPrefix,
        skipEmptyFolders,
      }) => (
        <FlexLayout flexDirection="row" space="8px">
          <Folder
            folder={{
              id,
              name,
              path: buildProviderPath(providerFolderPath, providerPrefix),
            }}
            isConcise={true}
            type="external"
          />
          {enableSkipEmptyFolders && skipEmptyFolders && (
            <Chip
              status="info"
              text={intl.formatMessage({
                id: 'StorageIntegrations.skipEmptyFolders',
                defaultMessage: 'Skip empty folders',
                description: 'Chip text indicating empty folders are skipped',
              })}
            />
          )}
        </FlexLayout>
      ),
    },
    {
      key: 'evisortFolderPath',
      cellType: 'filesystem',
      title: intl.formatMessage({
        id: 'StorageIntegrations.evisortFolder',
        defaultMessage: 'Evisort Folder',
        description: 'Column header for Evisort folder path',
      }),
      mapCellProps: ({ id, evisortFolderPath }) => ({
        folder: {
          id,
          name: intl.formatMessage({
            id: 'StorageIntegrations.documentsFolder',
            defaultMessage: 'Documents',
            description: 'Default folder name for documents',
          }),
          path: evisortFolderPath,
          type: 'internal',
          isConcise: true,
        },
      }),
    },
    {
      key: 'allowedSyncFileTypes',
      cellType: 'chips',
      title: intl.formatMessage({
        id: 'StorageIntegrations.fileTypes',
        defaultMessage: 'File Types',
        description: 'Column header for allowed file types',
      }),
      mapCellProps: ({ allowedSyncFileTypes }) => ({
        chips: fileTypesToChips(allowedSyncFileTypes || []),
      }),
      mapCellValue: ({ allowedSyncFileTypes }) =>
        (allowedSyncFileTypes || []).join(', '),
      width: 'l',
    },
    {
      key: 'dateCreated',
      cellType: 'datetime',
      title: intl.formatMessage({
        id: 'StorageIntegrations.created',
        defaultMessage: 'Created',
        description: 'Column header for creation date',
      }),
      mapCellProps: ({ dateCreated }) => ({
        datetime: dateCreated,
      }),
    },
  ];

  const { isLoadingSyncPairs } = useAsync(
    getSyncPairs,
    { clientId },
    {
      condition: true,
      errorToastMessage: intl.formatMessage({
        id: 'StorageIntegrations.fetchSyncPairsError',
        defaultMessage: 'Unable to fetch sync pairs, please try again.',
        description: 'Error message when failing to fetch sync pairs',
      }),
      successHandler: (syncPairs) => {
        dispatch(slices.integrations.actions.setSyncPairs(syncPairs));
      },
      intervalMs: 10000,
    },
  );

  useEffect(() => {
    const existingPairData = syncPairs?.find((el) => el.id === syncPairId);

    const isUpdatingPairData = !activeSyncPair && existingPairData;
    const isCreatingPairData = !activeSyncPair && !syncPairId;
    if (state) {
      if (code && (isCreatingPairData || isUpdatingPairData)) {
        const newPairData = createSyncPairData({
          ...storageIntegrations[provider],
        });
        const syncPairData = resetSyncPairAuthData(
          existingPairData || newPairData,
        );
        dispatch(
          slices.integrations.actions.setActiveSyncPair({
            ...syncPairData,
            state,
            code,
          }),
        );
        dispatch(
          slices.integrations.actions.setManageSyncPairStage(
            ManageSyncPairStageType.Authentication,
          ),
        );
      }
      if (!code) {
        dispatch(slices.integrations.actions.setActiveSyncPair(null));
        removeQueryParam(location, navigate, QueryParamType.States);
      }
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [states, syncPairs]);

  const [deleteSyncPairModal, showDeleteSyncPairModal] = useModal({
    actionButton: {
      variant: 'red',
      text: intl.formatMessage({
        id: 'StorageIntegrations.deleteButton',
        defaultMessage: 'Delete',
        description: 'Delete button text in confirmation modal',
      }),
      errorHandler: () => {
        showToast(
          ERROR,
          intl.formatMessage({
            id: 'StorageIntegrations.deleteSyncPairError',
            defaultMessage: 'Unable to delete sync pair, please try again.',
            description: 'Error message when failing to delete sync pair',
          }),
        );
      },
      promise: async () => {
        await deleteSyncPair(syncPairToDelete.id, clientId);
        dispatch(slices.integrations.actions.removeSyncPair(syncPairToDelete));
        showToast(
          SUCCESS,
          intl.formatMessage(
            {
              id: 'StorageIntegrations.deleteSyncPairSuccess',
              defaultMessage: '{name} sync pair has been deleted.',
              description:
                'Success message when sync pair is deleted. Example: name = "Google Drive Sync"',
            },
            { name: syncPairToDelete.name },
          ),
        );
      },
    },
    onHide: () => setSyncPairToDelete(null),
    title: intl.formatMessage({
      id: 'StorageIntegrations.deleteSyncPairTitle',
      defaultMessage: 'Delete Sync Pair?',
      description: 'Title for delete sync pair modal',
    }),
    content: (
      <FlexLayout flexDirection="column" space={2}>
        <Text color="gray-900" variant="s-spaced">
          {intl.formatMessage({
            id: 'StorageIntegrations.deleteConfirmationQuestion',
            defaultMessage: 'Are you sure you want to delete this sync pair?',
            description: 'Confirmation question for deleting sync pair',
          })}
        </Text>
        <Text color="gray-900" variant="s-spaced">
          {intl.formatMessage({
            id: 'StorageIntegrations.deleteWarningMessage',
            defaultMessage:
              'Deleting the sync pair will not stop an active file transfer, and will not delete any files that have been transfered.',
            description: 'Warning message about deletion consequences',
          })}{' '}
          <Text variant="s-dense-bold">
            {intl.formatMessage({
              id: 'StorageIntegrations.actionCannotBeReverted',
              defaultMessage: 'This action cannot be reverted.',
              description: 'Warning that the action cannot be undone',
            })}
          </Text>
        </Text>
      </FlexLayout>
    ),
  });

  const isLoading = isLoadingSyncPairs || (state && code);

  if (!isLoading && syncPairs.length === 0) {
    return <EmptySyncPairsPlaceholder />;
  }

  const handleHideConfigurationDetailsPanel = () => {
    setActiveConfig(null);
  };

  const rowActions = [
    {
      label: intl.formatMessage({
        id: 'StorageIntegrations.tryConnectingAgain',
        defaultMessage: 'Try connecting again',
        description: 'Row action to retry connection',
      }),
      onClick: async (d) => {
        const syncPair = await connectSyncPair(d);
        dispatch(slices.integrations.actions.addSyncPair(syncPair));
      },
      condition: (d) => {
        const providerErrors = [
          SyncPairStatusType.OtherError,
          SyncPairStatusType.DataAccessError,
          SyncPairStatusType.OutOfSpaceError,
        ];
        return providerErrors.includes(d.status);
      },
    },
    {
      label: intl.formatMessage({
        id: 'StorageIntegrations.reauthenticate',
        defaultMessage: 'Reauthenticate',
        description: 'Row action to reauthenticate sync pair',
      }),
      onClick: (d) => {
        dispatch(
          slices.integrations.actions.setActiveSyncPair({
            ...resetSyncPairAuthData(d),
            isReauthenticate: true,
          }),
        );
        dispatch(
          slices.integrations.actions.setManageSyncPairStage(
            ManageSyncPairStageType.Authentication,
          ),
        );
      },
      condition: (d) =>
        [SyncPairStatusType.Active, SyncPairStatusType.AuthError].includes(
          d.status,
        ),
    },
    {
      label: intl.formatMessage({
        id: 'StorageIntegrations.delete',
        defaultMessage: 'Delete',
        description: 'Row action to delete sync pair',
      }),
      onClick: (d) => {
        setSyncPairToDelete(d);
        showDeleteSyncPairModal();
      },
    },
    {
      label: intl.formatMessage({
        id: 'StorageIntegrations.configure',
        defaultMessage: 'Configure',
        description: 'Row action to configure sync pair',
      }),
      onClick: (d) => {
        setActiveConfig(d);
        setActiveTab('config');
      },
      condition: () => !!enableConfigPanel && testIsSuperAdmin(currentUser),
    },
    {
      label: intl.formatMessage({
        id: 'StorageIntegrations.errors',
        defaultMessage: 'Errors',
        description: 'Row action to view file events errors',
      }),
      onClick: (d) => {
        setActiveConfig(d);
        setActiveTab('errors');
      },
      condition: () => !!enableConfigPanel && testIsSuperAdmin(currentUser),
    },
  ];

  return (
    <>
      <Layout justify="flex-end" pb={4}>
        <Button
          text={intl.formatMessage({
            id: 'StorageIntegrations.addSyncPair',
            defaultMessage: 'Add Sync Pair',
            description: 'Button text to add new sync pair',
          })}
          variant="primary"
          onClick={() =>
            dispatch(
              slices.integrations.actions.setManageSyncPairStage(
                ManageSyncPairStageType.Provider,
              ),
            )
          }
        />
      </Layout>
      <PersistedTable
        context={TableContextType.SyncPairsManagement}
        state={{ pageIndex, pageSize }}
        name="storage-integrations"
        isLoading={isLoading}
        columns={columns}
        data={chunk(syncPairs, pageSize)[pageIndex - 1] || []}
        totalCount={syncPairs.length}
        rowActions={rowActions}
        options={{ enableManageColumns: false }}
        onPaginate={({ pageIndex }) => {
          dispatch(
            slices.integrations.actions.setManageSyncPairPageIndex({
              ...tableState,
              pageIndex,
            }),
          );
        }}
        onPageSizeChange={({ pageSize }) => {
          dispatch(
            slices.integrations.actions.setManageSyncPairPageSize({
              ...tableState,
              pageSize,
            }),
          );
        }}
      />
      {deleteSyncPairModal}
      {activeConfig && (
        <StorageConfigurationPanel
          config={activeConfig}
          tab={activeTab}
          onHide={handleHideConfigurationDetailsPanel}
        />
      )}
    </>
  );
}

export default StorageIntegrations;
